import React, { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import BiometricSecure from '../plugins/biometricSecure';

/**
 * BiometricUnlock — cold-launch re-auth gate.
 *
 * Rendered INSTEAD of the main softphone UI when the app cold-starts to the
 * foreground with a biometric-protected session already on-device. The token
 * is released from the secure store (Keychain / Keystore) only after a live
 * Face ID / Touch ID / fingerprint check.
 *
 * - "Unlock" calls getToken(): triggers the OS biometric prompt; resolves with
 *   the secure token only after the user authenticates.
 * - "Use password instead" clears the secure token (the plaintext copy is
 *   removed by the caller) and falls through to the existing sign-in screen,
 *   so password / SSO login remains fully available when biometrics are
 *   unavailable, un-enrolled, or declined.
 */
export default function BiometricUnlock({ onUnlocked, onUsePassword }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const platform = Capacitor.getPlatform();
  const isIos = platform === 'ios';

  const unlock = async () => {
    setBusy(true);
    setError('');
    try {
      const r = await BiometricSecure.getToken();
      if (r && r.auth && r.token) {
        onUnlocked(r.token);
        return;
      }
      setError(
        isIos
          ? 'Unable to unlock with Face ID / Touch ID.'
          : 'Unable to unlock with your fingerprint.',
      );
    } catch (e) {
      setError('Biometric unlock was cancelled or unavailable.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-header">
          <h1>Truva</h1>
          <p>Unlock to continue</p>
        </div>
        {error && <p className="login-error">{error}</p>}
        <button className="login-btn" onClick={unlock} disabled={busy}>
          {busy ? 'Checking…' : isIos ? 'Unlock with Face ID' : 'Unlock with fingerprint'}
        </button>
        <button
          className="login-btn"
          style={{ marginTop: 8 }}
          onClick={onUsePassword}
          disabled={busy}
        >
          Use password instead
        </button>
      </div>
    </div>
  );
}
